/**
 * Admin walk-in registration tab (Fase F).
 *
 * One-screen form that mirrors the public /reservar flow, used when a client
 * walks in / phones in and pays at the center. Captures the FULL CRM card,
 * health questionnaire, and three legal consents (signed in person on tablet
 * or paper) without going through Redsys.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, CheckCircle2, Copy, Tag, Save, Trash2, Ticket } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH_ITEMS = [
  { key: 'alergias_respiratorias', label: 'Alergias respiratorias' },
  { key: 'congestion_nasal', label: 'Congestión nasal frecuente' },
  { key: 'sensibilidad_respiratoria', label: 'Sensibilidad respiratoria' },
  { key: 'asma', label: 'Asma' },
  { key: 'bronquitis_recurrente', label: 'Bronquitis recurrente' },
  { key: 'problemas_piel', label: 'Problemas de piel' },
  { key: 'estres', label: 'Estrés' },
  { key: 'fiebre_contagiosa', label: 'Fiebre / enfermedad contagiosa' },
  { key: 'tratamiento_medico', label: 'En tratamiento médico (último año)' },
];

const initialState = {
  booking_type: 'solo_adultos',
  num_adults: 1,
  num_children: 0,
  num_babies: 0,
  room: '',
  date: new Date().toISOString().slice(0, 10),
  time: '10:00',
  duration_minutes: 60,
  service_id: '',
  customer: {
    full_name: '',
    birth_date: '',
    dni: '',
    phone: '',
    email: '',
    address: '',
    is_minor: false,
    guardian_name: '',
    guardian_dni: '',
    guardian_phone: '',
    notes: '',
  },
  health: HEALTH_ITEMS.reduce((a, i) => ({ ...a, [i.key]: false }), { otros: false, otros_texto: '' }),
  consents: {
    accepted_data_consent: false,
    accepted_disclaimer: false,
    accepted_cancellation_policy: false,
  },
  consents_signed_in_person: true,
  payment_method: 'cash',
  bono_code: '',
  notes: '',
  mark_paid: true,
  discount_promo_id: '',
  manual_price: '',
  discount_reason: '',
};

const slots = (() => {
  const out = [];
  for (let h = 9; h <= 21; h++) {
    for (const m of [0, 15, 30, 45]) {
      if (h === 21 && m > 0) continue;
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();

export const WalkInTab = ({ authHeader, services }) => {
  const [state, setState] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [signResult, setSignResult] = useState(null);
  const [promos, setPromos] = useState([]);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  // Active sign-link session created from the "Generar enlace de firma" button.
  // While this is set we poll the public signature endpoint to detect when
  // the client has finished signing on their device (or our tablet).
  const [activeSignSession, setActiveSignSession] = useState(null);
  // {booking_id, signature_token, signature_url, signed: bool}

  // Bono autocomplete (payment_method = 'bono')
  const [bonoResults, setBonoResults] = useState([]);
  const [bonoOpen, setBonoOpen] = useState(false);

  const searchBonos = async (query) => {
    const q = (query || '').trim();
    if (q.length < 2) {
      setBonoResults([]);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/admin/bonos/search?q=${encodeURIComponent(q)}`,
        { headers: { Authorization: authHeader } }
      );
      setBonoResults(res.data || []);
    } catch {
      setBonoResults([]);
    }
  };

  const selectBono = (b) => {
    setState((p) => ({
      ...p,
      bono_code: b.code,
      customer: {
        ...p.customer,
        full_name: p.customer.full_name || b.customer_name || '',
        phone: p.customer.phone || b.customer_phone || '',
        email: p.customer.email || b.customer_email || '',
      },
    }));
    setBonoOpen(false);
    setBonoResults([]);
    toast.success(`Bono ${b.code} asociado`);
  };

  const DRAFT_KEY = 'halo_walkin_draft_v1';

  // 1) On mount, restore any saved draft.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && saved.state) {
        setState({ ...initialState, ...saved.state });
        toast.success('Borrador recuperado — sigue donde lo dejaste', { duration: 4000 });
        if (saved.savedAt) setDraftSavedAt(saved.savedAt);
      }
    } catch (e) {
      // Corrupt draft → ignore
      localStorage.removeItem(DRAFT_KEY);
    }
  }, []);

  // 2) Auto-save to localStorage as the user types (debounced).
  useEffect(() => {
    // Don't persist the empty initial form
    const isEmpty =
      !state.customer.full_name &&
      !state.customer.phone &&
      !state.customer.email &&
      !state.notes &&
      !state.discount_reason;
    if (isEmpty) return;
    const handle = setTimeout(() => {
      try {
        const savedAt = new Date().toISOString();
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ state, savedAt }));
        setDraftSavedAt(savedAt);
      } catch (e) {
        // Quota exceeded — silent
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [state]);

  const clearDraft = () => {
    if (!window.confirm('¿Descartar el borrador? Se borrarán todos los datos del formulario.')) return;
    localStorage.removeItem(DRAFT_KEY);
    setState({ ...initialState, service_id: state.service_id });
    setDraftSavedAt(null);
    sigRef.current?.clear();
    toast.success('Borrador descartado');
  };

  useEffect(() => {
    if (!authHeader) return;
    axios
      .get(`${API}/admin/internal-promos`, { headers: { Authorization: authHeader } })
      .then((r) => setPromos((Array.isArray(r.data) ? r.data : []).filter((p) => p.active)))
      .catch(() => {});
  }, [authHeader]);

  useEffect(() => {
    if (services && services.length > 0 && !state.service_id) {
      setState((p) => ({ ...p, service_id: services[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  const setCustomer = (k, v) =>
    setState((p) => ({ ...p, customer: { ...p.customer, [k]: v } }));
  const setHealth = (k, v) =>
    setState((p) => ({ ...p, health: { ...p.health, [k]: v } }));
  const setConsent = (k, v) =>
    setState((p) => ({ ...p, consents: { ...p.consents, [k]: v } }));

  const allConsents = Object.values(state.consents).every((v) => v);

  const submit = async () => {
    if (!allConsents) {
      toast.error('El cliente debe firmar los tres consentimientos.');
      return;
    }
    if (!state.customer.full_name.trim() || !state.customer.phone.trim()) {
      toast.error('Nombre y teléfono son obligatorios.');
      return;
    }
    // Capture in-person signature if present (canvas in section 5)
    const hasSignature = sigRef.current && !sigRef.current.isEmpty();
    if (!hasSignature) {
      toast.error('Falta la firma del cliente en el recuadro.');
      return;
    }
    setSubmitting(true);
    try {
      const customer = { ...state.customer };
      Object.keys(customer).forEach((k) => {
        if (customer[k] === '' || customer[k] === undefined) delete customer[k];
      });
      const payload = {
        booking_type: state.booking_type,
        num_adults: state.num_adults,
        num_children: state.num_children,
        num_babies: state.num_babies,
        room: state.room || undefined,
        date: state.date,
        time: state.time,
        duration_minutes: state.duration_minutes,
        service_id: state.service_id || undefined,
        customer,
        health: state.health,
        consents: state.consents,
        consents_signed_in_person: state.consents_signed_in_person,
        payment_method: state.payment_method,
        bono_code: state.bono_code || undefined,
        notes: state.notes,
        mark_paid: state.mark_paid,
        discount_promo_id: state.discount_promo_id || undefined,
        manual_price: state.manual_price === '' ? undefined : parseFloat(state.manual_price),
        discount_reason: state.discount_reason || undefined,
        signature_image_b64: sigRef.current.toDataURL(),
      };
      const res = await axios.post(`${API}/admin/bookings/walk-in`, payload, {
        headers: { Authorization: authHeader },
      });
      setLastResult(res.data);
      toast.success(`Reserva creada: ${res.data.customer_number}`);
      localStorage.removeItem(DRAFT_KEY);
      setDraftSavedAt(null);
      setState({ ...initialState, service_id: state.service_id });
      sigRef.current?.clear();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear la reserva');
    } finally {
      setSubmitting(false);
    }
  };

  const sendForSigning = async () => {
    if (!state.customer.full_name.trim() || !state.customer.phone.trim()) {
      toast.error('Nombre y teléfono son obligatorios.');
      return;
    }
    if (!state.customer.email) {
      toast.error('Necesitamos el email del cliente para enviarle el enlace.');
      return;
    }
    setSubmitting(true);
    try {
      const customer = { ...state.customer };
      Object.keys(customer).forEach((k) => {
        if (customer[k] === '' || customer[k] === undefined) delete customer[k];
      });
      const payload = {
        booking_type: state.booking_type,
        num_adults: state.num_adults,
        num_children: state.num_children,
        num_babies: state.num_babies,
        room: state.room || undefined,
        date: state.date,
        time: state.time,
        duration_minutes: state.duration_minutes,
        service_id: state.service_id || undefined,
        customer,
        health: state.health,
        // Consents will be filled by the client when they sign; send false
        consents: {
          accepted_data_consent: false,
          accepted_disclaimer: false,
          accepted_cancellation_policy: false,
        },
        consents_signed_in_person: false,
        payment_method: state.payment_method,
        bono_code: state.bono_code || undefined,
        notes: state.notes,
        mark_paid: false,
        discount_promo_id: state.discount_promo_id || undefined,
        manual_price: state.manual_price === '' ? undefined : parseFloat(state.manual_price),
        discount_reason: state.discount_reason || undefined,
      };
      const res = await axios.post(
        `${API}/admin/bookings/walk-in/send-for-signing`,
        payload,
        { headers: { Authorization: authHeader } },
      );
      setSignResult(res.data);
      toast.success(`Enlace enviado a ${state.customer.email}`);
      localStorage.removeItem(DRAFT_KEY);
      setDraftSavedAt(null);
      setState({ ...initialState, service_id: state.service_id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar el enlace');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  /**
   * Generate a signing link using current form data and open it in a new
   * tab/window. The client reads the full legal texts on that page, signs
   * with their finger and closes. Meanwhile we poll the public signature
   * endpoint and switch the admin's UI to "Firmado" once it arrives.
   */
  const openSigningLink = async () => {
    if (!state.customer.full_name.trim() || !state.customer.phone.trim()) {
      toast.error('Nombre y teléfono son obligatorios.');
      return;
    }
    setSubmitting(true);
    try {
      const customer = { ...state.customer };
      Object.keys(customer).forEach((k) => {
        if (customer[k] === '' || customer[k] === undefined) delete customer[k];
      });
      const payload = {
        booking_type: state.booking_type,
        num_adults: state.num_adults,
        num_children: state.num_children,
        num_babies: state.num_babies,
        room: state.room || undefined,
        date: state.date,
        time: state.time,
        duration_minutes: state.duration_minutes,
        service_id: state.service_id || undefined,
        customer,
        health: state.health,
        consents: {
          accepted_data_consent: false,
          accepted_disclaimer: false,
          accepted_cancellation_policy: false,
        },
        consents_signed_in_person: false,
        payment_method: state.payment_method,
        bono_code: state.bono_code || undefined,
        notes: state.notes,
        mark_paid: false,
        discount_promo_id: state.discount_promo_id || undefined,
        manual_price: state.manual_price === '' ? undefined : parseFloat(state.manual_price),
        discount_reason: state.discount_reason || undefined,
      };
      const res = await axios.post(
        `${API}/admin/bookings/walk-in/send-for-signing`,
        payload,
        { headers: { Authorization: authHeader } },
      );
      setActiveSignSession({
        booking_id: res.data.booking_id,
        signature_token: res.data.signature_token,
        signature_url: res.data.signature_url,
        customer_number: res.data.customer_number,
        whatsapp_message: res.data.whatsapp_message,
        email_sent: res.data.email_sent,
        total_price: res.data.total_price,
        signed: false,
      });
      if (res.data.email_sent) {
        toast.success(`Enlace enviado por email a ${state.customer.email}`);
      } else {
        toast.info('Booking creado. Copia el enlace o envíalo por WhatsApp.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar el enlace de firma');
    } finally {
      setSubmitting(false);
    }
  };

  // Poll signature status while an active session is pending.
  useEffect(() => {
    if (!activeSignSession || activeSignSession.signed) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/signature/${activeSignSession.signature_token}`);
        if (r.data.already_signed) {
          setActiveSignSession((s) => (s ? { ...s, signed: true, signed_at: r.data.signed_at } : s));
          toast.success('✅ Cliente firmado — ahora cobra y factura');
        }
      } catch {
        /* token may have just expired — ignore */
      }
    }, 3000);
    return () => clearInterval(id);
  }, [activeSignSession]);

  const chargeAndCloseSession = async () => {
    if (!activeSignSession) return;
    if (!window.confirm(`¿Cobrar y emitir factura para ${state.customer.full_name || 'el cliente'}?`)) return;
    try {
      await axios.patch(
        `${API}/admin/bookings/${activeSignSession.booking_id}?status=completed`,
        {},
        { headers: { Authorization: authHeader } },
      );
      toast.success('Sesión cobrada · factura enviada por email');
      // Clear everything for the next client
      localStorage.removeItem(DRAFT_KEY);
      setDraftSavedAt(null);
      setActiveSignSession(null);
      setState({ ...initialState, service_id: state.service_id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al facturar');
    }
  };

  const cancelSignSession = () => {
    if (!window.confirm('¿Cancelar la firma en curso? La reserva quedará pendiente y podrás retomarla desde "Firmas".')) return;
    setActiveSignSession(null);
  };

  const downloadConsents = async () => {
    if (!lastResult?.booking_id) return;
    try {
      const res = await axios.get(
        `${API}/admin/bookings/${lastResult.booking_id}/consents-pdf`,
        { headers: { Authorization: authHeader }, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `consentimientos_${lastResult.customer_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar el PDF');
    }
  };

  return (
    <div className="space-y-6" data-testid="walk-in-tab">
      <div>
        <h2 className="text-2xl font-heading text-halo-charcoal flex items-center gap-2">
          <UserPlus size={22} /> Alta presencial
        </h2>
        <p className="text-sm text-halo-text">
          Registra a un cliente que paga en el centro: ficha + cuestionario de salud + consentimientos firmados.
        </p>
      </div>

      {/* Draft indicator — visible at all times so the user knows nothing is lost */}
      {draftSavedAt && (
        <div
          className="flex items-center justify-between gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5"
          data-testid="walkin-draft-bar"
        >
          <div className="flex items-center gap-2 text-sm text-green-900">
            <Save size={16} className="text-green-700" />
            <span>
              Borrador guardado{' '}
              <span className="text-green-700">
                ({new Date(draftSavedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })})
              </span>
            </span>
            <span className="hidden md:inline text-xs text-green-700">
              · Si recargas o cierras la página, los datos siguen aquí.
            </span>
          </div>
          <button
            type="button"
            onClick={clearDraft}
            className="text-xs text-red-600 hover:underline flex items-center gap-1"
            data-testid="walkin-clear-draft"
          >
            <Trash2 size={12} /> Descartar borrador
          </button>
        </div>
      )}

      {signResult && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-5 space-y-3" data-testid="sign-link-result">
          <div className="flex items-start gap-2">
            <Send className="text-blue-700 mt-1" size={20} />
            <div>
              <p className="text-blue-900 font-medium">
                Enlace generado · cliente {signResult.customer_number}
              </p>
              <p className="text-xs text-blue-800">
                Email {signResult.email_sent ? 'enviado ✓' : 'no enviado (sin email)'} ·
                {' '}Caduca {signResult.expires_at ? new Date(signResult.expires_at).toLocaleDateString('es-ES') : 'en 7 días'}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <p className="text-xs text-halo-text mb-1">Enlace para firmar:</p>
            <code className="text-xs text-halo-charcoal break-all block">
              {signResult.signature_url}
            </code>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => copyToClipboard(signResult.signature_url)}
              data-testid="copy-sign-url"
            >
              <Copy size={14} className="mr-1" /> Copiar enlace
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copyToClipboard(signResult.whatsapp_message)}
              data-testid="copy-whatsapp-msg"
            >
              <Copy size={14} className="mr-1" /> Copiar mensaje WhatsApp
            </Button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(signResult.whatsapp_message)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                Abrir en WhatsApp
              </Button>
            </a>
            <Button size="sm" variant="outline" onClick={() => setSignResult(null)}>
              Cerrar
            </Button>
          </div>
        </div>
      )}

      {lastResult && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-green-600 mt-0.5" size={22} />
            <div>
              <p className="text-green-900 font-medium">
                Reserva confirmada — {lastResult.customer_number}
              </p>
              <p className="text-xs text-green-800">
                Sala: {lastResult.room === 'sala_grande' ? 'Sala 2 (grande)' : 'Sala 1 (pequeña)'} ·
                Importe: {lastResult.total_price}€
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={downloadConsents} data-testid="download-consents-pdf">
            Descargar PDF firmas
          </Button>
        </div>
      )}

      {/* Booking type */}
      <section className="bg-white rounded-xl border border-halo-taupe/15 p-5 space-y-4">
        <h3 className="font-medium text-halo-charcoal">1. Tipo de reserva</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            { v: 'solo_adultos', l: 'Solo adultos' },
            { v: 'con_ninos', l: 'Con niños' },
            { v: 'adulto_bebe', l: 'Adulto + bebé' },
          ].map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setState((p) => ({ ...p, booking_type: t.v }))}
              className={`p-2 rounded-lg border-2 text-sm ${
                state.booking_type === t.v
                  ? 'border-halo-brown bg-halo-brown/5'
                  : 'border-halo-taupe/20'
              }`}
              data-testid={`walkin-type-${t.v}`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Adultos</Label>
            <Input type="number" min="1" value={state.num_adults}
              onChange={(e) => setState((p) => ({ ...p, num_adults: parseInt(e.target.value) || 1 }))}
              data-testid="walkin-adults" />
          </div>
          <div>
            <Label>Niños (≤14)</Label>
            <Input type="number" min="0" value={state.num_children}
              onChange={(e) => setState((p) => ({ ...p, num_children: parseInt(e.target.value) || 0 }))}
              data-testid="walkin-children" />
          </div>
          <div>
            <Label>Bebés</Label>
            <Input type="number" min="0" value={state.num_babies}
              onChange={(e) => setState((p) => ({ ...p, num_babies: parseInt(e.target.value) || 0 }))}
              data-testid="walkin-babies" />
          </div>
          <div>
            <Label>Sala (opcional)</Label>
            <select value={state.room}
              onChange={(e) => setState((p) => ({ ...p, room: e.target.value }))}
              className="w-full mt-1 rounded-md border border-halo-taupe/20 px-2 py-2 bg-white text-sm">
              <option value="">Auto-asignar</option>
              <option value="sala_grande">Sala 2 (grande)</option>
              <option value="sala_pequena">Sala 1 (pequeña)</option>
            </select>
          </div>
        </div>
      </section>

      {/* Date / Time */}
      <section className="bg-white rounded-xl border border-halo-taupe/15 p-5 space-y-4">
        <h3 className="font-medium text-halo-charcoal">2. Fecha y hora</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Fecha</Label>
            <Input type="date" value={state.date}
              onChange={(e) => setState((p) => ({ ...p, date: e.target.value }))}
              data-testid="walkin-date" />
          </div>
          <div>
            <Label>Hora</Label>
            <select value={state.time}
              onChange={(e) => setState((p) => ({ ...p, time: e.target.value }))}
              className="w-full mt-1 rounded-md border border-halo-taupe/20 px-2 py-2 bg-white text-sm"
              data-testid="walkin-time">
              {slots.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label>Duración (min)</Label>
            <Input type="number" value={state.duration_minutes}
              onChange={(e) => setState((p) => ({ ...p, duration_minutes: parseInt(e.target.value) || 60 }))} />
          </div>
          <div>
            <Label>Servicio</Label>
            <select value={state.service_id}
              onChange={(e) => setState((p) => ({ ...p, service_id: e.target.value }))}
              className="w-full mt-1 rounded-md border border-halo-taupe/20 px-2 py-2 bg-white text-sm">
              {(services || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Customer */}
      <section className="bg-white rounded-xl border border-halo-taupe/15 p-5 space-y-3">
        <h3 className="font-medium text-halo-charcoal">3. Ficha del cliente</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Nombre y apellidos *</Label>
            <Input value={state.customer.full_name}
              onChange={(e) => setCustomer('full_name', e.target.value)}
              data-testid="walkin-full-name" />
          </div>
          <div>
            <Label>DNI / NIE</Label>
            <Input value={state.customer.dni}
              onChange={(e) => setCustomer('dni', e.target.value)} />
          </div>
          <div>
            <Label>Teléfono *</Label>
            <Input value={state.customer.phone}
              onChange={(e) => setCustomer('phone', e.target.value)}
              data-testid="walkin-phone" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={state.customer.email}
              onChange={(e) => setCustomer('email', e.target.value)} />
          </div>
          <div>
            <Label>Fecha de nacimiento</Label>
            <Input type="date" value={state.customer.birth_date}
              onChange={(e) => setCustomer('birth_date', e.target.value)} />
          </div>
          <div>
            <Label>Dirección</Label>
            <Input value={state.customer.address}
              onChange={(e) => setCustomer('address', e.target.value)} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={state.customer.is_minor}
            onChange={(e) => setCustomer('is_minor', e.target.checked)} />
          Menor de edad — añadir tutor legal
        </label>
        {state.customer.is_minor && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 bg-amber-50 p-3 rounded-lg">
            <Input placeholder="Nombre tutor" value={state.customer.guardian_name}
              onChange={(e) => setCustomer('guardian_name', e.target.value)} />
            <Input placeholder="DNI tutor" value={state.customer.guardian_dni}
              onChange={(e) => setCustomer('guardian_dni', e.target.value)} />
            <Input placeholder="Tlf tutor" value={state.customer.guardian_phone}
              onChange={(e) => setCustomer('guardian_phone', e.target.value)} />
          </div>
        )}
      </section>

      {/* Health */}
      <section className="bg-white rounded-xl border border-halo-taupe/15 p-5 space-y-2">
        <h3 className="font-medium text-halo-charcoal">4. Cuestionario de salud</h3>
        <p className="text-xs text-halo-text">
          Marca lo que el cliente declare. <strong>El admin puede registrar al cliente igualmente</strong>;
          el bloqueo automático solo aplica al flujo online.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {HEALTH_ITEMS.map((it) => (
            <label key={it.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={state.health[it.key] || false}
                onChange={(e) => setHealth(it.key, e.target.checked)}
                data-testid={`walkin-health-${it.key}`} />
              {it.label}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2">
          <input type="checkbox" checked={state.health.otros}
            onChange={(e) => setHealth('otros', e.target.checked)} />
          <span className="text-sm">Otros:</span>
          <Input disabled={!state.health.otros} className="h-8 text-sm"
            value={state.health.otros_texto}
            onChange={(e) => setHealth('otros_texto', e.target.value)} />
        </div>
      </section>

      {/* Payment */}
      <section className="bg-white rounded-xl border border-halo-taupe/15 p-5 space-y-3">
        <h3 className="font-medium text-halo-charcoal">5. Pago</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            { v: 'cash', l: 'Efectivo' },
            { v: 'card_at_center', l: 'Tarjeta (datafono)' },
            { v: 'bono', l: 'Bono' },
          ].map((p) => (
            <button key={p.v} type="button"
              onClick={() => setState((s) => ({ ...s, payment_method: p.v }))}
              className={`p-2 rounded-lg border-2 text-sm ${
                state.payment_method === p.v
                  ? 'border-halo-brown bg-halo-brown/5'
                  : 'border-halo-taupe/20'
              }`}>
              {p.l}
            </button>
          ))}
        </div>
        {state.payment_method === 'bono' && (
          <div className="relative">
            <Input
              placeholder="Buscar bono por código, nombre, email o teléfono..."
              value={state.bono_code}
              onChange={(e) => {
                const v = e.target.value;
                setState((p) => ({ ...p, bono_code: v }));
                setBonoOpen(true);
                searchBonos(v);
              }}
              onFocus={() => {
                setBonoOpen(true);
                if (state.bono_code) searchBonos(state.bono_code);
              }}
              onBlur={() => setTimeout(() => setBonoOpen(false), 200)}
              data-testid="walkin-bono-search"
            />
            {bonoOpen && bonoResults.length > 0 && (
              <div
                className="absolute left-0 right-0 top-full mt-1 bg-white border border-halo-taupe/20 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50"
                data-testid="walkin-bono-results"
              >
                {bonoResults.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectBono(b);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-halo-cream/50 border-b border-halo-taupe/10 last:border-b-0"
                    data-testid={`walkin-bono-option-${b.code}`}
                  >
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-mono font-bold text-halo-brown flex items-center gap-1">
                        <Ticket size={12} /> {b.code}
                      </span>
                      <span className="text-xs text-halo-text">
                        {b.sessions_remaining}/{b.sessions_total} ses
                      </span>
                    </div>
                    <div className="text-xs text-halo-text">
                      {b.customer_name} · {b.customer_phone} · {b.bono_name}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {bonoOpen &&
              bonoResults.length === 0 &&
              (state.bono_code || '').trim().length >= 2 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-halo-taupe/20 rounded-lg shadow-sm px-3 py-2 text-xs text-halo-text z-50">
                  No hay bonos activos con sesiones disponibles para
                  «{state.bono_code}»
                </div>
              )}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={state.mark_paid}
            onChange={(e) => setState((p) => ({ ...p, mark_paid: e.target.checked }))} />
          Marcar como pagado ahora
        </label>
        <Textarea placeholder="Notas internas..." value={state.notes}
          onChange={(e) => setState((p) => ({ ...p, notes: e.target.value }))}
          className="text-sm" rows={2} />
      </section>

      {/* Internal discount — not shown on public web */}
      <section className="bg-white rounded-xl border border-halo-taupe/15 p-5 space-y-3" data-testid="walkin-discount-block">
        <h3 className="font-medium text-halo-charcoal flex items-center gap-2">
          <Tag size={16} /> Descuento interno <span className="text-xs font-normal text-halo-text">(no aparece en la web)</span>
        </h3>
        <p className="text-xs text-halo-text">
          Para flyer, promociones puntuales, cortesía... Se reflejará en la factura.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Aplicar promo interna</Label>
            <select
              value={state.discount_promo_id}
              onChange={(e) => {
                const id = e.target.value;
                const p = promos.find((x) => x.id === id);
                setState((s) => ({
                  ...s,
                  discount_promo_id: id,
                  discount_reason: p ? p.label : s.discount_reason,
                }));
              }}
              className="w-full mt-1 rounded-md border border-halo-taupe/20 px-2 py-2 bg-white text-sm"
              data-testid="walkin-promo-select"
            >
              <option value="">— Sin promo / personalizado —</option>
              {promos.filter((p) => !p.expired).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `[${p.code}] ` : ''}
                  {p.label} ({p.discount_type === 'percent'
                    ? `-${p.discount_value}%`
                    : p.discount_type === 'final_price'
                    ? `→ ${p.discount_value}€`
                    : `-${p.discount_value}€`})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Precio final manual (€) <span className="text-xs font-normal text-halo-text">opcional</span></Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Ej: 20.00"
              value={state.manual_price}
              onChange={(e) => setState((p) => ({ ...p, manual_price: e.target.value }))}
              data-testid="walkin-manual-price"
            />
          </div>
        </div>
        <div>
          <Label>Motivo / etiqueta del descuento</Label>
          <Input
            placeholder="Ej: Flyer marzo, cortesía, promoción mayo..."
            value={state.discount_reason}
            onChange={(e) => setState((p) => ({ ...p, discount_reason: e.target.value }))}
            data-testid="walkin-discount-reason"
          />
          <p className="text-xs text-halo-text mt-1">
            Aparecerá en la factura como línea de descuento. Si dejas vacío y eliges una promo, usaremos el nombre de la promo.
          </p>
        </div>
      </section>

      {/* Action block — always-on. The button never blocks: each click
          creates a NEW signing request and sends a fresh link by email
          (and produces a ready-to-paste WhatsApp message). */}
      <div className="bg-halo-cream rounded-xl p-5 border border-halo-taupe/15 space-y-4" data-testid="walkin-action-block">
        <div>
          <h3 className="font-medium text-halo-charcoal flex items-center gap-2">
            📨 Enviar enlace de firma al cliente
          </h3>
          <p className="text-xs text-halo-text mt-1">
            Crea la reserva y envía un enlace al cliente para que lea los textos legales y firme con
            el dedo. Puedes pulsar el botón cuantas veces necesites — siempre se envía un enlace nuevo.
          </p>
        </div>

        {!state.customer.full_name.trim() || !state.customer.phone.trim() ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            <strong>Antes de enviar, completa al menos:</strong>
            <ul className="ml-4 list-disc mt-0.5">
              {!state.customer.full_name.trim() && <li>Nombre del cliente</li>}
              {!state.customer.phone.trim() && <li>Teléfono</li>}
            </ul>
          </div>
        ) : null}

        <Button
          onClick={openSigningLink}
          className="w-full bg-halo-brown hover:bg-halo-charcoal text-halo-cream py-5 text-base"
          data-testid="walkin-send-link"
        >
          {submitting
            ? 'Generando enlace...'
            : activeSignSession
              ? '↻ Enviar un enlace nuevo'
              : '📨 Crear reserva y enviar enlace de firma'}
        </Button>

        {activeSignSession && !activeSignSession.signed && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 space-y-3" data-testid="walkin-sign-pending">
            <div className="flex items-center gap-2 text-blue-900">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
              <strong>Esperando firma del cliente · {activeSignSession.customer_number}</strong>
            </div>
            <p className="text-xs text-blue-800">
              {activeSignSession.email_sent
                ? `Email enviado a ${state.customer.email || 'el cliente'}.`
                : 'Email no se envió (sin email del cliente). Copia el enlace o usa WhatsApp.'}
            </p>
            <div className="bg-white rounded-lg p-2 border border-blue-200 break-all text-xs font-mono">
              {activeSignSession.signature_url}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(activeSignSession.signature_url)}
                data-testid="copy-sign-url"
              >
                <Copy size={14} className="mr-1" /> Copiar enlace
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(activeSignSession.whatsapp_message)}
                data-testid="copy-whatsapp-msg"
              >
                <Copy size={14} className="mr-1" /> Copiar mensaje WhatsApp
              </Button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(activeSignSession.whatsapp_message)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                  Abrir WhatsApp
                </Button>
              </a>
            </div>
            <p className="text-xs text-blue-700">
              Cuando firme verás aquí "✅ Cliente firmado" y podrás cobrar.
            </p>
          </div>
        )}

        {activeSignSession && activeSignSession.signed && (
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 space-y-3" data-testid="walkin-sign-done">
            <div className="flex items-center gap-2 text-green-900">
              <CheckCircle2 size={20} />
              <strong>Cliente firmado · {activeSignSession.customer_number}</strong>
            </div>
            <p className="text-xs text-green-800">
              Firmado a las {activeSignSession.signed_at
                ? new Date(activeSignSession.signed_at).toLocaleString('es-ES')
                : 'ahora mismo'}.
              Total a cobrar: <strong>{activeSignSession.total_price}€</strong>.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={chargeAndCloseSession}
                className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
                data-testid="walkin-charge-session"
              >
                💶 Cobrar y emitir factura
              </Button>
              <Button variant="outline" onClick={() => setActiveSignSession(null)}>
                Cerrar (cobrar más tarde)
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalkInTab;
